import { splitEncoding } from '@contentful/live-preview';
import type { SearchClient } from 'algoliasearch';
import type { GetServerSideProps } from 'next';
import { renderToString } from 'react-dom/server';
import {
  getServerState,
  InstantSearch,
  InstantSearchSSRProvider,
  useHits,
  usePagination,
} from 'react-instantsearch-core';

import { parseUiStateFilters } from '@/utils/algolia';
import { findMarketingPartnerCard } from '@/utils/findMarketingPartnerCard';
import * as localeUtils from '@/utils/locale';
import { extractPathFromUrl } from '@/utils/urlUtils';

import { Configure } from '@/components/Configure';
import type { ContentfulCollectionGroupFragment } from '@/graphql/contentful/api';
import {
  applyProductConfigurationFilters,
  buildUiStateFromURL,
  getIndexName,
  ProductConfigurationFilterType,
} from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import type { WatchHit } from '@/integrations/algolia/types';
import {
  fetchCmsPageDataWithExtraData,
  fetchListingPageData,
  fetchSelectorModalByMarketingPartner,
} from '@/integrations/contentful';
import { getUserTokenForAlgoliaSSR } from '@/lib/algoliaUserToken';
import { commonServerSideProps } from '@/lib/commonPageStaticProps';
import {
  FILTERS_FETCH_LIMIT,
  HITS_COLLECTION_PER_PAGE,
  NEXT_PUBLIC_DEFAULT_APP_LOCALE,
} from '@/lib/const';
import { resolveContentDraftMode } from '@/lib/draftMode';
import { capture404Event } from '@/lib/errors';
import { isSupportedLocale } from '@/lib/locales/locales';
import { PLP_ERRORS } from '@/lib/notFoundErrors';
import {
  getContentfulRegionFromLocale,
  getGqlRegionFromLocale,
  getRegionFromLocale,
} from '@/lib/regions';
import { CacheHeaderType, setCacheHeader } from '@/lib/revalidate';
import { fetchSelectedCollections } from '@/views/AllTheWatchesView/helpers';
import { resolveCollectionGroups } from '@/views/EditorialView/data-helpers';
import { EditorialViewWrapper } from '@/views/EditorialView/EditorialViewWrapper';

export function DummyGenericWatchesPage({
  algoliaCategoryId,
  algoliaRuleName,
  analyticsTags,
  client,
  filters,
  indexName,
  page,
  query,
  userToken,
}: {
  algoliaCategoryId?: string;
  algoliaRuleName?: string;
  analyticsTags?: string[];
  client: SearchClient;
  filters: string;
  indexName: string;
  locale?: string;
  page?: number;
  query?: string;
  userToken?: string;
}) {
  // Default analytics tags for editorial pages, can be overridden
  const defaultAnalyticsTags = [
    'platform:web',
    'product:watches',
    'page:listing',
    'context:editorial',
    ...(algoliaRuleName ? ['rule:' + algoliaRuleName] : []),
    ...(algoliaCategoryId ? ['category:' + algoliaCategoryId] : []),
  ];

  return (
    <InstantSearchSSRProvider>
      <InstantSearch
        indexName={indexName}
        insights={false}
        searchClient={client}
      >
        <Configure
          analyticsTags={analyticsTags || defaultAnalyticsTags}
          clickAnalytics={true}
          distinct={true}
          facetingAfterDistinct={true}
          facets={['*']}
          filters={filters}
          hitsPerPage={20}
          page={page}
          query={query}
          userToken={userToken}
        />
        {/*
          Note: We intentionally don't include DummyFacetComponents here.
          The FiltersModal on the client is lazy-loaded (only renders when opened),
          so including refinement list widgets here would cause a widget mismatch
          during hydration, triggering an extra search when widgets are "removed".
        */}
        <DummyHitsComponent />
        <DummyPaginationComponent />
      </InstantSearch>
    </InstantSearchSSRProvider>
  );
}

function DummyHitsComponent() {
  const { hits } = useHits<WatchHit>();
  return <>{hits?.map((hit) => hit.objectID)}</>;
}

function DummyPaginationComponent() {
  // Register pagination widget to match client-side HitsPagination
  usePagination();
  return null;
}

export const getServerSideProps: GetServerSideProps = commonServerSideProps(
  async (context) => {
    const { params, query, resolvedUrl } = context;
    const draftMode = resolveContentDraftMode(context);
    const routerLocale = localeUtils.getLocaleFromParams(context?.params);

    if (!isSupportedLocale(routerLocale)) {
      return { notFound: true };
    }

    if (!params?.slug) {
      capture404Event('Watch Collection PLP', {
        requestUrl: context.req.url ? extractPathFromUrl(context.req.url) : '',
        reason: PLP_ERRORS.PLP_SLUG_MISSING,
      });
      return { notFound: true };
    }

    try {
      const editorialPageName = params.slug[0];
      const region = getRegionFromLocale(routerLocale);
      const contentfulLocale = region.locale.contentful ?? region.locale.locale;
      const indexName = getIndexName(
        'PRODUCT',
        routerLocale ?? NEXT_PUBLIC_DEFAULT_APP_LOCALE,
      );

      const initialUiState = buildUiStateFromURL(
        resolvedUrl,
        indexName,
        undefined,
        undefined,
        region.locale,
      );

      const { countryCode } = getContentfulRegionFromLocale(routerLocale);

      const {
        algoliaCategoryId,
        algoliaRuleName,
        cmsData: editorialCmsData,
        cmsDefaultLayoutData,
        seoConfigBuilder,
      } = await fetchListingPageData(
        'watch editorial page',
        contentfulLocale,
        null,
        editorialPageName,
        null,
        countryCode,
        region.locale.code,
        draftMode,
      );

      if (!editorialCmsData || editorialCmsData.length === 0) {
        // handle some cms page but route is watches/xxxx like: watches/gifting,watches/emergency,watches/mybreitling
        const { channel, languageCode } = getGqlRegionFromLocale(routerLocale);
        const slugArr = ['watches', ...((params?.slug ?? []) as string[])];

        const slug = slugArr.join('/');

        const {
          breadcrumb,
          cmsData,
          cmsDefaultLayoutData,
          cmsExtraData,
          seoConfigBuilder,
        } = await fetchCmsPageDataWithExtraData(
          slug,
          contentfulLocale,
          languageCode,
          channel,
          countryCode,
          draftMode,
        );

        if (!cmsData || cmsData.length === 0) {
          return {
            notFound: true,
          };
        }
        return {
          props: {
            cmsData: cmsData,
            cmsExtraDataRecord: cmsExtraData,
            cmsDefaultLayoutData,
            breadcrumb,
            seoConfig: seoConfigBuilder?.build(),
          },
        };
      }

      const selectedCollectionsNames: string[] = await fetchSelectedCollections(
        editorialCmsData,
        region.country.channelSlug,
        region.locale.code,
      );

      const collectionFilters = selectedCollectionsNames
        ?.map((colName) => `collection:'${colName}'`)
        .join(' OR ');

      let algoliaFilters = applyProductConfigurationFilters(
        ProductConfigurationFilterType.PLP,
        'Watch',
        draftMode,
        collectionFilters,
      );

      const algoliaCategoryIdIsNotNullOrEmpty =
        algoliaCategoryId && algoliaCategoryId !== '';

      if (algoliaCategoryIdIsNotNullOrEmpty) {
        algoliaFilters =
          algoliaFilters + ` AND category_page_id:'${algoliaCategoryId}'`;
      }

      const algoliaSsrSearchClient = createSearchClient({
        isPreview: draftMode,
        isSsr: true,
      });

      const index = algoliaSsrSearchClient.initIndex(indexName);

      // Perform a search request to get nbPages and facets for translation
      const searchResponse = await index.search('', {
        filters: algoliaFilters,
        facets: ['*'],
        maxValuesPerFacet: FILTERS_FETCH_LIMIT,
        hitsPerPage: HITS_COLLECTION_PER_PAGE,
      });

      // Translate URL filter values from English to the target locale using
      // searchResponse facets, so that the server-side Algolia search uses the
      // correct locale-specific values.
      const translatedUiState = buildUiStateFromURL(
        resolvedUrl,
        indexName,
        undefined,
        undefined,
        region.locale,
        {
          initialResults: {
            [indexName]: {
              results: [
                {
                  facets: searchResponse.facets,
                  renderingContent: searchResponse.renderingContent,
                },
              ],
            },
          },
        } as any,
        'EN_US',
      );

      // Convert translated URL query parameters to Algolia filters
      const urlFilters = parseUiStateFilters(translatedUiState[indexName]);

      // Combine collection filters with URL filters
      const combinedFilters = urlFilters
        ? algoliaFilters
          ? `${algoliaFilters} AND ${urlFilters}`
          : urlFilters
        : algoliaFilters;

      const pageIndex = query?.page && parseInt(query.page as string);
      if (
        typeof pageIndex === 'number' &&
        (pageIndex > searchResponse.nbPages || pageIndex < 1)
      ) {
        capture404Event('Watch Collection PLP', {
          requestUrl: context.req.url
            ? extractPathFromUrl(context.req.url)
            : '',
          reason: PLP_ERRORS.PLP_PAGINATION_OUT_OF_RANGE,
        });
        return {
          notFound: true,
        };
      }

      // Generate Algolia user token server-side for consistent SSR/client rendering
      const algoliaUserToken = getUserTokenForAlgoliaSSR(context.req) ?? null;

      const serverState = await getServerState(
        <DummyGenericWatchesPage
          algoliaCategoryId={algoliaCategoryId}
          algoliaRuleName={algoliaRuleName}
          filters={combinedFilters}
          indexName={indexName}
          userToken={algoliaUserToken ?? undefined}
          {...(typeof pageIndex === 'number' ? { page: pageIndex - 1 } : {})}
          {...(algoliaRuleName ? { query: algoliaRuleName } : {})}
          client={algoliaSsrSearchClient}
        />,
        {
          renderToString,
        },
      );

      // Inject facets from the unfiltered search into serverState for translation
      // This ensures facets are available even when URL filters don't match any products
      if (serverState.initialResults?.[indexName]?.results?.[0]) {
        serverState.initialResults[indexName].results[0].facets =
          searchResponse.facets;
      }

      let resolvedCollectionGroups = null;
      try {
        resolvedCollectionGroups = await resolveCollectionGroups(
          editorialCmsData.filter(
            (d) => d.__typename === 'CollectionGroup',
          ) as ContentfulCollectionGroupFragment[],
          context.params?.lang as string,
          draftMode ?? false,
          algoliaUserToken ?? undefined,
        );
      } catch (e) {
        console.error('Failed to resolve collection groups', e);
      }

      const marketingPartnerCard = findMarketingPartnerCard(editorialCmsData);
      const marketingPartnerName = splitEncoding(
        (marketingPartnerCard as any)?.url ?? '',
      ).cleaned;

      const marketingSelectorModalResult =
        await fetchSelectorModalByMarketingPartner(
          marketingPartnerName,
          draftMode,
          contentfulLocale,
        );

      setCacheHeader(CacheHeaderType.NO_CACHE_TAG, context);
      return {
        props: {
          isEditorialView: true,
          algoliaCategoryId,
          algoliaRuleName,
          algoliaFilters,
          algoliaUserToken,
          draftMode: draftMode ?? false,
          pageName: editorialPageName ?? '',
          instantSearchProps: {
            initialUiState,
            indexName,
            serverState,
          },
          editorialCmsData,
          cmsDefaultLayoutData,
          seoConfig: seoConfigBuilder?.build(),
          serverState,
          resolvedCollectionGroups: resolvedCollectionGroups,
          marketingSelectorModalResult: marketingSelectorModalResult,
        },
      };
    } catch (error) {
      console.error('Failed to fetch server-side props', error);
      return { notFound: true };
    }
  },
);

export default EditorialViewWrapper;
